/**
 * ISLAMIC STORY BOOK — Frontend JS
 * DonateDirectly.com Custom Section
 * Version: 1.0.0 (Demo — Frontend Only)
 *
 * Features:
 *  - Stars canvas animation
 *  - Scroll reveal
 *  - Photo upload & local preview
 *  - Form data stored in localStorage
 *  - 7-page animated book with page-flip
 *  - Mobile swipe support
 *  - Thumbnail nav
 *  - Smooth scroll
 */

(function () {
  'use strict';

  /* ─── CONFIG ─────────────────────────────────────────── */
  const TOTAL_PAGES = 7;
  const LS_KEY = 'isb_book_data';

  /* ─── STATE ──────────────────────────────────────────── */
  let currentPage = 1;
  let isFlipping = false;
  let bookData = loadBookData();

  /* ─── DOM CACHE ──────────────────────────────────────── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  /* ═════════════════════════════════════════════════════
     1. STARS CANVAS
  ══════════════════════════════════════════════════════ */
  function initStars() {
    const canvas = document.getElementById('isb-stars-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, stars = [];

    function resize() {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 160; i++) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        r: Math.random() * 1.6 + 0.2,
        opacity: Math.random() * 0.7 + 0.2,
        speed: Math.random() * 0.003 + 0.001,
        phase: Math.random() * Math.PI * 2
      });
    }

    function drawStars(t) {
      ctx.clearRect(0, 0, W, H);
      stars.forEach(s => {
        const o = s.opacity + Math.sin(t * s.speed + s.phase) * 0.3;
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(245,200,66,${Math.max(0, Math.min(1, o))})`;
        ctx.fill();
      });
    }

    let t = 0;
    function frame() {
      t += 0.5;
      drawStars(t);
      requestAnimationFrame(frame);
    }
    frame();
  }

  /* ═════════════════════════════════════════════════════
     2. SCROLL REVEAL
  ══════════════════════════════════════════════════════ */
  function initReveal() {
    const els = $$('.isb-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('isb-visible'));
      return;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('isb-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
  }

  /* ═════════════════════════════════════════════════════
     3. SMOOTH SCROLL
  ══════════════════════════════════════════════════════ */
  function initSmoothScroll() {
    $$('.isb-scroll-to').forEach(a => {
      a.addEventListener('click', function (e) {
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ═════════════════════════════════════════════════════
     4. PHOTO UPLOADS
  ══════════════════════════════════════════════════════ */
  function initUploads() {
    const zones = $$('.isb-upload-zone');
    zones.forEach(zone => {
      const inputId = zone.getAttribute('data-target');
      const input = document.getElementById(inputId);
      const previewId = inputId.replace('isb-', 'isb-').replace('-photo', '-preview');
      const preview = document.getElementById(
        inputId === 'isb-cover-photo'  ? 'isb-cover-preview'  :
        inputId === 'isb-child-photo'  ? 'isb-child-preview'  :
        'isb-family-preview'
      );

      zone.addEventListener('click', () => input && input.click());

      // Drag & drop
      zone.addEventListener('dragover', e => {
        e.preventDefault();
        zone.style.borderColor = 'var(--isb-gold)';
      });
      zone.addEventListener('dragleave', () => {
        zone.style.borderColor = '';
      });
      zone.addEventListener('drop', e => {
        e.preventDefault();
        zone.style.borderColor = '';
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) handleFile(file, zone, preview, inputId);
      });

      if (input) {
        input.addEventListener('change', function () {
          if (this.files[0]) handleFile(this.files[0], zone, preview, inputId);
        });
      }
    });

    // Restore from state
    if (bookData.coverPhotoUrl && document.getElementById('isb-cover-preview')) {
      showPreviewFromUrl(document.getElementById('isb-cover-preview'), document.getElementById('isb-cover-upload-zone'));
    }
  }

  function handleFile(file, zone, previewEl, inputId) {
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = function (e) {
      const url = e.target.result;
      if (previewEl) {
        previewEl.src = url;
        previewEl.style.display = 'block';
        zone.classList.add('isb-has-image');
        const inner = zone.querySelector('.isb-upload-zone__inner');
        if (inner) inner.style.display = 'none';
      }
      // Store in bookData
      const key = inputId === 'isb-cover-photo'  ? 'coverPhotoUrl'  :
                  inputId === 'isb-child-photo'   ? 'childPhotoUrl'  :
                  'familyPhotoUrl';
      bookData[key] = url;
      saveBookData();
    };
    reader.readAsDataURL(file);
  }

  function showPreviewFromUrl(previewEl, zone) {
    previewEl.style.display = 'block';
    zone && zone.classList.add('isb-has-image');
    const inner = zone && zone.querySelector('.isb-upload-zone__inner');
    if (inner) inner.style.display = 'none';
  }

  /* ═════════════════════════════════════════════════════
     5. FORM
  ══════════════════════════════════════════════════════ */
  function initForm() {
    const form   = document.getElementById('isb-personalize-form');
    const btn    = document.getElementById('isb-generate-btn');
    const reset  = document.getElementById('isb-reset-btn');
    if (!form) return;

    // Restore saved values
    if (bookData.bookTitle)   setVal('isb-book-title', bookData.bookTitle);
    if (bookData.childName)   setVal('isb-child-name', bookData.childName);
    if (bookData.storyTheme) {
      const radio = form.querySelector(`input[name="story_theme"][value="${bookData.storyTheme}"]`);
      if (radio) radio.checked = true;
    }

    // Live update mockup name
    const nameInput = document.getElementById('isb-child-name');
    if (nameInput) {
      nameInput.addEventListener('input', function () {
        updateMockupName(this.value);
      });
      if (bookData.childName) updateMockupName(bookData.childName);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const name = getVal('isb-child-name').trim();
      if (!name) {
        flashInput('isb-child-name');
        return;
      }
      // Animate button
      const btnText   = btn.querySelector('.isb-btn-text');
      const btnLoader = btn.querySelector('.isb-btn-loader');
      btn.disabled = true;
      if (btnText)   btnText.style.display = 'none';
      if (btnLoader) btnLoader.style.display = 'inline-flex';

      // Gather data
      bookData.bookTitle   = getVal('isb-book-title').trim() || 'The Journey of ' + name;
      bookData.childName   = name;
      bookData.storyTheme  = getCheckedRadio(form, 'story_theme');
      saveBookData();

      setTimeout(() => {
        btn.disabled = false;
        if (btnText)   btnText.style.display = 'inline';
        if (btnLoader) btnLoader.style.display = 'none';

        // Update book
        currentPage = 1;
        renderBook();
        renderThumbs();
        updatePreviewNameDisplay();
        updateMockupName(name);

        // Scroll to preview
        const preview = document.getElementById('isb-preview');
        if (preview) preview.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 900);
    });

    reset.addEventListener('click', function () {
      if (!confirm('Reset all your story details?')) return;
      bookData = getDefaultData();
      saveBookData();
      form.reset();
      // Clear previews
      ['isb-cover-preview','isb-child-preview','isb-family-preview'].forEach(id => {
        const el = document.getElementById(id);
        if (el) { el.style.display = 'none'; el.src = ''; }
      });
      $$('.isb-upload-zone').forEach(z => {
        z.classList.remove('isb-has-image');
        const inner = z.querySelector('.isb-upload-zone__inner');
        if (inner) inner.style.display = 'block';
      });
      currentPage = 1;
      renderBook();
      renderThumbs();
      updatePreviewNameDisplay();
      updateMockupName('Your Child');
    });
  }

  function getVal(id) { const el = document.getElementById(id); return el ? el.value : ''; }
  function setVal(id, v) { const el = document.getElementById(id); if (el) el.value = v; }
  function getCheckedRadio(form, name) {
    const r = form.querySelector(`input[name="${name}"]:checked`);
    return r ? r.value : "Prophets' Stories";
  }
  function flashInput(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.style.borderColor = '#ef4444';
    el.focus();
    setTimeout(() => el.style.borderColor = '', 2000);
  }
  function updateMockupName(name) {
    const el = document.getElementById('isb-mockup-name');
    if (el) el.textContent = name || 'Your Child';
    const titleEl = document.getElementById('isb-mockup-title');
    if (titleEl) {
      titleEl.innerHTML = 'The Journey of<br><em id="isb-mockup-name">' + (name || 'Your Child') + '</em>';
    }
  }
  function updatePreviewNameDisplay() {
    const el = document.getElementById('isb-preview-name-display');
    if (el) {
      el.textContent = bookData.childName
        ? `Previewing the story of ${bookData.childName} — turn the pages below!`
        : 'Enter your child\'s name above to personalise this preview.';
    }
  }

  /* ═════════════════════════════════════════════════════
     6. PAGE CONTENT BUILDERS
  ══════════════════════════════════════════════════════ */
  const ISLAMIC_STORIES = [
    {
      title: 'The Light of Patience',
      theme: 'A lesson inspired by the patience of Prophet Ayyub (AS)',
      verse: '"And We will surely test you with something of fear and hunger and loss... but give good tidings to the patient." — Quran 2:155',
      symbol: '🌄',
      scene: 'A golden dawn breaking over rolling hills, where a figure walks a long, winding path toward the light — a journey of faith and endurance.'
    },
    {
      title: 'The Wisdom of Generosity',
      theme: 'A lesson inspired by the generosity of Prophet Ibrahim (AS)',
      verse: '"Who is it that would loan Allah a goodly loan so He may multiply it for him many times over?" — Quran 2:245',
      symbol: '🕊️',
      scene: 'A lush garden where hands extend gifts of food and warmth to travellers, showing the beauty of open-handed giving.'
    },
    {
      title: 'The Journey of Trust',
      theme: 'A lesson inspired by the trust of Prophet Yusuf (AS)',
      verse: '"And whoever relies upon Allah — then He is sufficient for him." — Quran 65:3',
      symbol: '🌿',
      scene: 'Stars shining brightly over a desert landscape — a reminder that even in darkness, the light of faith guides the way.'
    },
    {
      title: 'The Power of Gratitude',
      theme: 'A reflection on Shukr — the Islamic virtue of thankfulness',
      verse: '"If you are grateful, I will surely increase you in favour." — Quran 14:7',
      symbol: '☀️',
      scene: 'A community gathering under a great tree, sharing a meal together in gratitude for Allah\'s blessings.'
    }
  ];

  function getStoryForTheme(theme) {
    const map = {
      "Prophets' Stories": 1,
      "Kindness & Charity": 1,
      "Islamic Values": 2,
      "Daily Duas": 0,
      "Ramadan Journey": 3,
      "Good Character": 3,
      "Helping Others": 1,
      "Stories of Compassion": 0
    };
    const idx = map[theme] !== undefined ? map[theme] : 0;
    return ISLAMIC_STORIES[idx];
  }

  function buildPageHTML(pageNum) {
    const name  = bookData.childName || 'your child';
    const title = bookData.bookTitle || `The Journey of ${name}`;
    const theme = bookData.storyTheme || "Prophets' Stories";
    const story = getStoryForTheme(theme);

    switch (pageNum) {
      case 1: return buildCoverPage(title, name);
      case 2: return buildBeginningPage(name);
      case 3: return buildLessonPage(name);
      case 4: return buildFamilyPage(name);
      case 5: return buildIslamicPage(story, name);
      case 6: return buildDeedsPage(name);
      case 7: return buildEndingPage(name);
      default: return '';
    }
  }

  function buildCoverPage(title, name) {
    const coverImg = bookData.coverPhotoUrl
      ? `<img class="isb-page-cover-img" src="${bookData.coverPhotoUrl}" alt="Cover">`
      : '';
    return `
      <div class="isb-page-inner isb-page--cover">
        ${coverImg}
        <div class="isb-page-geo"></div>
        <div class="isb-cover-content">
          <div class="isb-cover-crescent" aria-hidden="true">🌙</div>
          <div class="isb-cover-stars" aria-hidden="true">✦ ✦ ✦ ✦ ✦</div>
          <h2 class="isb-cover-title">${escHtml(title)}</h2>
          <div class="isb-cover-name">${escHtml(name)}</div>
          <div class="isb-cover-sub">A Story Inspired by Islamic Values</div>
          <div class="isb-cover-stars" aria-hidden="true">✦ ✦ ✦ ✦ ✦</div>
        </div>
        <div class="isb-cover-geo-bottom"></div>
      </div>`;
  }

  function buildBeginningPage(name) {
    const photo = bookData.childPhotoUrl
      ? `<div class="isb-page-scene"><img src="${bookData.childPhotoUrl}" alt="${escHtml(name)}"></div>`
      : `<div class="isb-page-scene" role="img" aria-label="Child illustration">🌟</div>`;
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">Chapter One</span>
          <span class="isb-page-num">2</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:16px;color:#1a6b3c;margin-bottom:12px;">The Beginning</h3>
        <p class="isb-page-quote">
          One peaceful evening, <em>${escHtml(name)}</em> discovered that kindness
          and helping others brings light into people's lives — a light that never fades.
        </p>
        ${photo}
        <p class="isb-page-quote" style="font-size:12px;color:#7c6b5a;margin-top:8px;">
          Every great journey begins with a single step of goodness.
        </p>
        <div class="isb-page-ornament">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book</div>
      </div>`;
  }

  function buildLessonPage(name) {
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">Chapter Two</span>
          <span class="isb-page-num">3</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:16px;color:#1a6b3c;margin-bottom:12px;">The Lesson</h3>
        <p class="isb-page-quote">
          <em>${escHtml(name)}</em> learned that every small act of generosity
          can make a big difference in someone's world.
        </p>
        <div class="isb-page-scene" style="background:linear-gradient(135deg,#e8f5ec,#f0ebe0);">
          <div style="text-align:center;padding:12px;">
            <div style="font-size:32px;margin-bottom:6px;">🤲 ❤️ 🕌</div>
            <p style="font-size:11px;color:#5a7060;font-style:italic;line-height:1.5;">"The believers, in their mutual love and mercy, are like a single body." — Hadith</p>
          </div>
        </div>
        <p class="isb-page-quote" style="font-size:12px;color:#7c6b5a;">
          Compassion, charity, and care for others are the hallmarks of a beautiful character.
        </p>
        <div class="isb-page-ornament">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book</div>
      </div>`;
  }

  function buildFamilyPage(name) {
    const photo = bookData.familyPhotoUrl
      ? `<div class="isb-page-scene"><img src="${bookData.familyPhotoUrl}" alt="Family"></div>`
      : `<div class="isb-page-scene" style="background:linear-gradient(135deg,#f5ede0,#e8e0d0);">🏡</div>`;
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">Chapter Three</span>
          <span class="isb-page-num">4</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:16px;color:#1a6b3c;margin-bottom:12px;">A Family Moment</h3>
        ${photo}
        <p class="isb-page-quote">
          With the encouragement and love of family, <em>${escHtml(name)}</em>
          shared goodness with others — for a home filled with love is the greatest blessing.
        </p>
        <p class="isb-page-quote" style="font-size:12px;color:#7c6b5a;margin-top:8px;">
          "The best of you are those who are best to their families." — Prophet Muhammad ﷺ
        </p>
        <div class="isb-page-ornament">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book</div>
      </div>`;
  }

  function buildIslamicPage(story, name) {
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">Chapter Four</span>
          <span class="isb-page-num">5</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:15px;color:#1a6b3c;margin-bottom:10px;">${escHtml(story.title)}</h3>
        <div class="isb-page-scene" style="background:linear-gradient(135deg,#1a2e1a,#0d3320);height:110px;flex-shrink:0;">
          <div style="text-align:center;">
            <div style="font-size:36px;">${story.symbol}</div>
            <p style="font-size:10px;color:rgba(255,255,255,0.6);margin-top:6px;padding:0 12px;line-height:1.5;">${escHtml(story.scene)}</p>
          </div>
        </div>
        <p class="isb-page-quote" style="font-size:12px;font-style:italic;color:#5a4a3a;border-left:2px solid #c9972b;padding-left:10px;margin:10px 0;">
          ${escHtml(story.verse)}
        </p>
        <p class="isb-page-quote" style="font-size:12px;color:#3d2c1e;">
          <em>${escHtml(name)}</em> carried this lesson in their heart — that faith and patience
          always light the way, even in the darkest hours.
        </p>
        <div class="isb-page-ornament">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book</div>
      </div>`;
  }

  function buildDeedsPage(name) {
    const stars = Array.from({length: 12}, (_, i) =>
      `<span class="isb-star-item" style="animation-delay:${i * 0.08}s">⭐</span>`
    ).join('');
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">Chapter Five</span>
          <span class="isb-page-num">6</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:16px;color:#1a6b3c;margin-bottom:12px;">A Book of Good Deeds</h3>
        <p class="isb-page-quote">
          Every act of kindness becomes part of <em>${escHtml(name)}</em>'s beautiful story —
          written not in ink, but in the hearts of those touched by their goodness.
        </p>
        <div class="isb-page-stars-anim" role="img" aria-label="Stars for good deeds">
          ${stars}
        </div>
        <p class="isb-page-quote" style="font-size:12px;color:#7c6b5a;text-align:center;">
          "Not a word does one utter, except that there is a watcher nearby." — Quran 50:18
        </p>
        <div class="isb-page-ornament">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book</div>
      </div>`;
  }

  function buildEndingPage(name) {
    return `
      <div class="isb-page-inner isb-page--inner">
        <div class="isb-page-header">
          <span class="isb-page-chapter">The End</span>
          <span class="isb-page-num">7</span>
        </div>
        <h3 style="font-family:Georgia,serif;font-size:16px;color:#1a6b3c;margin-bottom:16px;">A Blessed Ending</h3>
        <div style="font-size:36px;text-align:center;margin-bottom:12px;">🤲</div>
        <p class="isb-page-quote" style="text-align:center;font-size:15px;">
          May <em>${escHtml(name)}</em> continue growing in kindness, generosity, and good character —
          a light for those around them, and a joy to all who know them.
        </p>
        <p style="text-align:center;font-size:13px;color:#c9972b;font-style:italic;margin:12px 0;font-family:Georgia,serif;">
          آمين — Ameen
        </p>
        <div style="text-align:center;">
          <a href="#isb-form" class="isb-page-ending-btn isb-scroll-to">
            ✨ Create My Real Story Book
          </a>
        </div>
        <div class="isb-page-ornament" style="margin-top:12px;">✦ ✦ ✦</div>
        <div class="isb-page-footer">Islamic Story Book · DonateDirectly.com</div>
      </div>`;
  }

  function buildBackPage(pageNum) {
    // Left side shows a decorative back-of-page for the previous spread
    if (pageNum <= 1) {
      return `<div class="isb-page-inner" style="background:#f5f0e6;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:16px;">
        <div class="isb-page-back-pattern"></div>
        <div style="font-size:32px;opacity:0.3;position:relative;z-index:1;">🌙</div>
        <p style="font-size:11px;color:#bbb;letter-spacing:2px;text-transform:uppercase;position:relative;z-index:1;">Islamic Story Book</p>
      </div>`;
    }
    return `<div class="isb-page-inner isb-page--inner" style="opacity:0.6;filter:blur(0.5px);">
      <div class="isb-page-back-pattern"></div>
      <div style="display:flex;align-items:center;justify-content:center;height:100%;opacity:0.2;font-size:48px;">✦</div>
    </div>`;
  }

  /* ═════════════════════════════════════════════════════
     7. BOOK RENDER & FLIP
  ══════════════════════════════════════════════════════ */
  function renderBook() {
    const rightEl = document.getElementById('isb-page-current');
    const leftEl  = document.getElementById('isb-page-back');
    if (rightEl) rightEl.innerHTML = buildPageHTML(currentPage);
    if (leftEl)  leftEl.innerHTML  = buildBackPage(currentPage);

    // Reinit smooth scroll for ending page btn
    $$('.isb-scroll-to').forEach(a => {
      if (!a._isbBound) {
        a._isbBound = true;
        a.addEventListener('click', function (e) {
          const t = document.querySelector(this.getAttribute('href'));
          if (t) { e.preventDefault(); t.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        });
      }
    });

    updateCounter();
    updateNavBtns();
    updateActiveThumbs();
  }

  function flipToPage(targetPage) {
    if (isFlipping || targetPage === currentPage) return;
    if (targetPage < 1 || targetPage > TOTAL_PAGES) return;
    isFlipping = true;

    const flipEl      = document.getElementById('isb-page-flip');
    const flipFront   = document.getElementById('isb-flip-front');
    const flipBack    = document.getElementById('isb-flip-back');
    const rightEl     = document.getElementById('isb-page-current');

    const direction = targetPage > currentPage ? 'next' : 'prev';

    if (direction === 'next') {
      // Flip front shows current page, back shows next
      flipFront.innerHTML = buildPageHTML(currentPage);
      flipBack.innerHTML  = buildPageHTML(targetPage);
      flipEl.style.transform = 'rotateY(0deg)';
      flipEl.style.display = 'block';

      requestAnimationFrame(() => {
        flipEl.style.transition = 'transform 0.65s cubic-bezier(0.645,0.045,0.355,1)';
        flipEl.style.transform = 'rotateY(-180deg)';
      });

      setTimeout(() => {
        currentPage = targetPage;
        renderBook();
        flipEl.style.display = 'none';
        flipEl.style.transition = '';
        flipEl.style.transform = '';
        isFlipping = false;
      }, 660);
    } else {
      // Reverse flip
      flipFront.innerHTML = buildPageHTML(targetPage);
      flipBack.innerHTML  = buildPageHTML(currentPage);
      flipEl.style.transform = 'rotateY(-180deg)';
      flipEl.style.display = 'block';

      requestAnimationFrame(() => {
        flipEl.style.transition = 'transform 0.65s cubic-bezier(0.645,0.045,0.355,1)';
        flipEl.style.transform = 'rotateY(0deg)';
      });

      setTimeout(() => {
        currentPage = targetPage;
        renderBook();
        flipEl.style.display = 'none';
        flipEl.style.transition = '';
        flipEl.style.transform = 'rotateY(-180deg)';
        isFlipping = false;
      }, 660);
    }
  }

  function updateCounter() {
    const cur = document.getElementById('isb-current-page-num');
    const tot = document.getElementById('isb-total-pages');
    if (cur) cur.textContent = currentPage;
    if (tot) tot.textContent = TOTAL_PAGES;
  }

  function updateNavBtns() {
    const prev = document.getElementById('isb-prev-page');
    const next = document.getElementById('isb-next-page');
    if (prev) prev.disabled = currentPage <= 1;
    if (next) next.disabled = currentPage >= TOTAL_PAGES;
  }

  /* ═════════════════════════════════════════════════════
     8. NAV BUTTONS & SWIPE
  ══════════════════════════════════════════════════════ */
  function initBookNav() {
    const prev = document.getElementById('isb-prev-page');
    const next = document.getElementById('isb-next-page');
    if (prev) prev.addEventListener('click', () => flipToPage(currentPage - 1));
    if (next) next.addEventListener('click', () => flipToPage(currentPage + 1));

    // Keyboard
    document.addEventListener('keydown', function (e) {
      const inFocus = document.activeElement;
      const isInput = inFocus && (inFocus.tagName === 'INPUT' || inFocus.tagName === 'TEXTAREA');
      if (isInput) return;
      if (e.key === 'ArrowRight') flipToPage(currentPage + 1);
      if (e.key === 'ArrowLeft')  flipToPage(currentPage - 1);
    });

    // Touch swipe
    const book = document.getElementById('isb-book');
    if (!book) return;
    let touchStartX = 0;
    book.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    book.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) {
        if (dx < 0) flipToPage(currentPage + 1);
        else        flipToPage(currentPage - 1);
      }
    });
  }

  /* ═════════════════════════════════════════════════════
     9. THUMBNAILS
  ══════════════════════════════════════════════════════ */
  const PAGE_META = [
    { label: 'Cover',       icon: '🌙' },
    { label: 'Beginning',   icon: '🌟' },
    { label: 'The Lesson',  icon: '📖' },
    { label: 'Family',      icon: '🏡' },
    { label: 'Islamic',     icon: '🕌' },
    { label: 'Good Deeds',  icon: '⭐' },
    { label: 'Ending',      icon: '🤲' }
  ];

  function renderThumbs() {
    const strip = document.getElementById('isb-thumbs-strip');
    if (!strip) return;
    strip.innerHTML = '';
    PAGE_META.forEach((p, i) => {
      const num = i + 1;
      const div = document.createElement('div');
      div.className = 'isb-thumb' + (num === currentPage ? ' isb-thumb--active' : '');
      div.setAttribute('role', 'button');
      div.setAttribute('tabindex', '0');
      div.setAttribute('aria-label', `Go to page ${num}: ${p.label}`);
      div.innerHTML = `
        <span class="isb-thumb__num">${num}</span>
        <span class="isb-thumb__icon">${p.icon}</span>
        <span class="isb-thumb__label">${p.label}</span>`;
      div.addEventListener('click', () => flipToPage(num));
      div.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') flipToPage(num); });
      strip.appendChild(div);
    });
  }

  function updateActiveThumbs() {
    $$('.isb-thumb').forEach((el, i) => {
      el.classList.toggle('isb-thumb--active', i + 1 === currentPage);
    });
  }

  /* ═════════════════════════════════════════════════════
     10. LOCALSTORAGE
  ══════════════════════════════════════════════════════ */
  function getDefaultData() {
    return { bookTitle: '', childName: '', storyTheme: "Prophets' Stories",
             coverPhotoUrl: '', childPhotoUrl: '', familyPhotoUrl: '' };
  }
  function loadBookData() {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? Object.assign(getDefaultData(), JSON.parse(saved)) : getDefaultData();
    } catch { return getDefaultData(); }
  }
  function saveBookData() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(bookData)); } catch {}
  }

  /* ═════════════════════════════════════════════════════
     UTIL
  ══════════════════════════════════════════════════════ */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ═════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════ */
  function init() {
    initStars();
    initReveal();
    initSmoothScroll();
    initUploads();
    initForm();
    renderBook();
    renderThumbs();
    initBookNav();
    updatePreviewNameDisplay();
    if (bookData.childName) updateMockupName(bookData.childName);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
